import React from 'react';
import { ShoppingBag, Tag, Star, ArrowRight, ShieldCheck, Truck, Package } from 'lucide-react';

export const MarketplacePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-brand-midnight text-white">
      <main className="pt-16">
        {/* Banner Section */}
        <section className="bg-brand-charcoal/30 border-b border-white/10 overflow-hidden relative">
           <div className="absolute inset-0 bg-glow-mesh-dark opacity-20"></div>
           <div className="container mx-auto px-6 py-24 flex flex-col md:flex-row items-center gap-16 relative z-10">
              <div className="flex-1">
                 <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-midnight text-[10px] font-black uppercase tracking-widest rounded-full mb-8 shadow-glow-gold">
                    Lançamento Exclusivo
                 </span>
                 <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight font-display uppercase tracking-tight">
                    Equipamentos <br/>
                    <span className="text-brand-gold">Pro Grade</span>
                 </h1>
                 <p className="text-xl text-white/40 mb-12 max-w-xl font-medium">
                    Curadoria especializada de varas, carretilhas e iscas testadas pelos melhores pescadores da nossa comunidade.
                 </p>
                 <div className="flex flex-wrap gap-5">
                    <button className="px-10 py-5 bg-brand-orange text-white font-black rounded-xl shadow-glow-orange hover:scale-105 transition-all flex items-center gap-3 font-display uppercase tracking-widest text-sm">
                       Ver Ofertas <ArrowRight size={20} />
                    </button>
                    <button className="px-10 py-5 bg-white/5 text-white/40 font-black rounded-xl border border-white/10 hover:bg-white/10 hover:text-white transition-all font-display uppercase tracking-widest text-sm">
                       Vender Equipamento
                    </button>
                 </div>
              </div>
              <div className="flex-1 relative">
                 <div className="w-full aspect-square bg-gradient-to-tr from-brand-gold/10 to-transparent rounded-full blur-3xl absolute inset-0"></div>
                 <div className="relative bg-white/5 backdrop-blur-3xl border border-white/10 p-10 rounded-[2.5rem] shadow-2xl">
                    <div className="flex justify-between items-start mb-8">
                       <div className="p-4 bg-brand-gold/10 rounded-2xl">
                          <ShoppingBag className="text-brand-gold" size={36} />
                       </div>
                       <div className="flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase">
                          <Package size={14} /> EM ESTOQUE
                       </div>
                    </div>
                    <h3 className="text-3xl font-black text-white mb-3 font-display uppercase">Iscas Artificiais Premium</h3>
                    <p className="text-white/40 text-base mb-8 font-medium">Kit com 5 unidades selecionadas para Grandes Tucunarés.</p>
                    <div className="flex items-center justify-between">
                       <span className="text-4xl font-black text-white font-display">R$ 249,90</span>
                       <button 
                         aria-label="Adicionar ao carrinho"
                         className="p-5 bg-brand-gold text-brand-midnight rounded-2xl hover:scale-110 transition-transform shadow-glow-gold"
                       >
                          <ShoppingBag size={28} />
                       </button>
                    </div>
                 </div>
              </div>
           </div>
        </section>

        {/* Categories Section */}
        <section className="py-24 container mx-auto px-6">
           <div className="flex items-center justify-between mb-16">
              <h2 className="text-4xl font-black text-white font-display uppercase tracking-tight">Categorias em Destaque</h2>
              <button className="text-brand-gold font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:underline">
                 Ver todas <ArrowRight size={16} />
              </button>
           </div>
           
           <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                 { name: 'Varas & Molinetes', icon: '🎣', color: 'bg-blue-500/5 text-blue-400 border-blue-500/10' },
                 { name: 'Iscas Artificiais', icon: '🐟', color: 'bg-orange-500/5 text-orange-400 border-orange-500/10' },
                 { name: 'Vestuário UV', icon: '👕', color: 'bg-green-500/5 text-green-400 border-green-500/10' },
                 { name: 'Camping & Outros', icon: '🏕️', color: 'bg-purple-500/5 text-purple-400 border-purple-500/10' }
              ].map(cat => (
                 <div key={cat.name} className={`p-10 rounded-[2rem] ${cat.color} border cursor-pointer hover:scale-105 transition-transform text-center group`}>
                    <span className="text-5xl mb-6 block group-hover:scale-110 transition-transform">{cat.icon}</span>
                    <h4 className="font-black text-xs uppercase tracking-widest">{cat.name}</h4>
                 </div>
              ))}
           </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white/5 py-24 border-t border-white/10">
           <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-16">
              <div className="flex items-start gap-6">
                 <div className="shrink-0 p-5 bg-brand-gold/10 rounded-2xl text-brand-gold shadow-glow-gold/10">
                    <ShieldCheck size={36} />
                 </div>
                 <div>
                    <h4 className="font-black text-white mb-3 uppercase tracking-widest text-sm">Compra Garantida</h4>
                    <p className="text-sm text-white/40 leading-relaxed font-medium">Seu dinheiro protegido pela comunidade Nossa Pesca.</p>
                 </div>
              </div>
              <div className="flex items-start gap-6">
                 <div className="shrink-0 p-5 bg-brand-gold/10 rounded-2xl text-brand-gold shadow-glow-gold/10">
                    <Truck size={36} />
                 </div>
                 <div>
                    <h4 className="font-black text-white mb-3 uppercase tracking-widest text-sm">Frete Especializado</h4>
                    <p className="text-sm text-white/40 leading-relaxed font-medium">Entrega segura para equipamentos frágeis em todo o país.</p>
                 </div>
              </div>
              <div className="flex items-start gap-6">
                 <div className="shrink-0 p-5 bg-brand-gold/10 rounded-2xl text-brand-gold shadow-glow-gold/10">
                    <Star size={36} />
                 </div>
                 <div>
                    <h4 className="font-black text-white mb-3 uppercase tracking-widest text-sm">Avaliação de Experts</h4>
                    <p className="text-sm text-white/40 leading-relaxed font-medium">Produtos revisados por quem realmente entende de pesca.</p>
                 </div>
              </div>
           </div>
        </section>
      </main>
    </div>
  );
};
